import type { ThemeControlledProperty } from './detectStyleChange'

/**
 * The data attribute used to store preserved style properties.
 * Contains a comma-separated list of CSS property names that should not be
 * overwritten by theme styles.
 */
export const PRESERVE_STYLES_ATTR = 'data-preserve-styles'

/**
 * Adds a CSS property to an element's preserve list, marking it as user-customized.
 * The preserve list prevents theme styles from overwriting user modifications.
 *
 * @param element - The DOM element to mark
 * @param property - The CSS property name to preserve (e.g., 'font-family', 'color')
 */
export const addPreservedStyle = (
  element: HTMLElement,
  property: ThemeControlledProperty,
): void => {
  const currentPreserved = getPreservedStyles(element)

  if (!currentPreserved.has(property)) {
    currentPreserved.add(property)
    const newValue = Array.from(currentPreserved).join(',')
    element.setAttribute(PRESERVE_STYLES_ATTR, newValue)
  }
}

/**
 * Removes a CSS property from an element's preserve list.
 * Use this when resetting styles or when theme styles should take over again.
 *
 * @param element - The DOM element to update
 * @param property - The CSS property name to remove from preservation
 */
export const removePreservedStyle = (
  element: HTMLElement,
  property: ThemeControlledProperty,
): void => {
  const currentPreserved = getPreservedStyles(element)

  if (currentPreserved.has(property)) {
    currentPreserved.delete(property)

    if (currentPreserved.size === 0) {
      element.removeAttribute(PRESERVE_STYLES_ATTR)
    } else {
      const newValue = Array.from(currentPreserved).join(',')
      element.setAttribute(PRESERVE_STYLES_ATTR, newValue)
    }
  }
}

/**
 * Gets the set of preserved style properties for an element.
 *
 * @param element - The DOM element to check
 * @returns A Set of CSS property names that are preserved
 */
export const getPreservedStyles = (element: HTMLElement): Set<string> => {
  const attrValue = element.getAttribute(PRESERVE_STYLES_ATTR)
  if (!attrValue) return new Set()
  return new Set(
    attrValue
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean),
  )
}

/**
 * Clears all preserved styles from an element.
 * Use this when the user wants to reset to theme defaults.
 *
 * @param element - The DOM element to clear
 */
export const clearPreservedStyles = (element: HTMLElement): void => {
  element.removeAttribute(PRESERVE_STYLES_ATTR)
}

/**
 * Checks if an element has any preserved styles.
 *
 * @param element - The DOM element to check
 * @returns true if the element has at least one preserved style
 */
export const hasPreservedStyles = (element: HTMLElement): boolean => {
  return element.hasAttribute(PRESERVE_STYLES_ATTR)
}

/**
 * Checks if a specific style property is preserved on an element.
 *
 * @param element - The DOM element to check
 * @param property - The CSS property name to check
 * @returns true if the property is preserved
 */
export const isStylePreserved = (
  element: HTMLElement,
  property: ThemeControlledProperty,
): boolean => {
  return getPreservedStyles(element).has(property)
}
